import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { authAPI, notificationAPI } from '../services/api';

const AuthContext = createContext(null);
let socket = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orderUpdates, setOrderUpdates] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('cust_token');
    const stored = localStorage.getItem('cust_user');
    if (token && stored) {
      setUser(JSON.parse(stored));
      setIsAuthenticated(true);
      authAPI.getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('cust_user', JSON.stringify(res.data));
          connectSocket(token);
        })
        .catch(logout)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectSocket = (token) => {
    if (socket) socket.disconnect();
    socket = io(process.env.REACT_APP_SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socket.on('connect', () => console.log('🔌 Socket connected'));
    socket.on('notification:new', () => setUnreadNotifications((n) => n + 1));
    socket.on('order:confirmed', (data) => setOrderUpdates((prev) => [...prev, { ...data, event: 'confirmed' }]));
    socket.on('order:preparing', (data) => setOrderUpdates((prev) => [...prev, { ...data, event: 'preparing' }]));
    socket.on('order:ready', (data) => setOrderUpdates((prev) => [...prev, { ...data, event: 'ready' }]));
    socket.on('order:picked_up', (data) => setOrderUpdates((prev) => [...prev, { ...data, event: 'picked_up' }]));
    socket.on('order:delivery_status', (data) => setOrderUpdates((prev) => [...prev, { ...data, event: 'delivery_status' }]));

    notificationAPI.getUnreadCount()
      .then(res => setUnreadNotifications(res.data.count))
      .catch(() => {});
  };

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { user: u, token } = res.data;
    if (u.role !== 'customer') throw new Error('Please use the correct portal for your role.');
    localStorage.setItem('cust_token', token);
    localStorage.setItem('cust_user', JSON.stringify(u));
    setUser(u);
    setIsAuthenticated(true);
    connectSocket(token);
    return u;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    const { user: u, token } = res.data;
    localStorage.setItem('cust_token', token);
    localStorage.setItem('cust_user', JSON.stringify(u));
    setUser(u);
    setIsAuthenticated(true);
    connectSocket(token);
    return u;
  }, []);

  const logout = useCallback(() => {
    if (socket) { socket.disconnect(); socket = null; }
    localStorage.removeItem('cust_token');
    localStorage.removeItem('cust_user');
    setUser(null);
    setIsAuthenticated(false);
    setOrderUpdates([]);
  }, []);

  const trackOrder = useCallback((orderId) => {
    socket?.emit('order:track', orderId);
  }, []);

  const getSocket = () => socket;

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, register, logout, orderUpdates, unreadNotifications, setUnreadNotifications, trackOrder, getSocket }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
